import React from "react";
import { observe } from "../utils/sharedObserver";
import { isRenderableImageSrc } from "../../../utils/imageUtils";

const MAX_RETRIES = 3;

/**
 * @param {string} url
 * @param {string} alt
 * @param {string} className Applied to the wrapper; size the image through it
 * @param {boolean} isDarkMode
 * @param {"cover"|"contain"} [fit] How the image fills the wrapper (default cover)
 */
const LazyImage = ({ url, alt, className, isDarkMode, fit = "cover" }) => {
  // Last line of defence: a non-absolute src is resolved against the current
  // page, so the browser would fetch it from our own server (once per retry).
  // Callers filter these out; render the broken-image box rather than trusting.
  const isUsable = isRenderableImageSrc(url);
  const [isInView, setIsInView] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);
  const imgRef = React.useRef(null);
  const retryTimerRef = React.useRef(null);

  React.useEffect(() => {
    if (!imgRef.current) return;
    return observe(imgRef.current, () => setIsInView(true));
  }, [url]);

  React.useEffect(() => {
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  const handleError = () => {
    if (retryCount < MAX_RETRIES) {
      const delay = Math.pow(2, retryCount + 1) * 1000; // 2s, 4s, 8s
      retryTimerRef.current = setTimeout(() => {
        setRetryCount((c) => c + 1);
      }, delay);
    } else {
      console.log("[IMAGE-LOADER] Failed to load image after retries:", url);
      setHasError(true);
      setIsLoaded(true);
    }
  };

  const handleLoad = () => {
    setIsLoaded(true);
  };

  return (
    <div ref={imgRef} className={className}>
      {!isInView ? (
        <div className={`w-full h-full flex items-center justify-center rounded-lg ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}>
          <span className="text-xs opacity-50">...</span>
        </div>
      ) : hasError || !isUsable ? (
        <div className={`w-full h-full flex items-center justify-center rounded-lg ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}>
          <span className="text-xs">❌</span>
        </div>
      ) : (
        <>
          {!isLoaded && (
            <div className={`absolute inset-0 flex items-center justify-center rounded-lg ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}>
              <span className="text-xs opacity-50">...</span>
            </div>
          )}
          <img
            key={retryCount}
            src={url}
            alt={alt}
            referrerPolicy="no-referrer"
            className={`w-full h-full ${fit === "contain" ? "object-contain" : "object-cover"} rounded-lg ${isLoaded ? "opacity-100" : "opacity-0"} transition-opacity duration-200`}
            onError={handleError}
            onLoad={handleLoad}
          />
        </>
      )}
    </div>
  );
};

export default LazyImage;
